import { debounce, findLast, get, isEqual, merge, reduce } from 'lodash';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { system } from 'styled-system';
import { mergeRefs } from '@hivebrite/utils';
import { useTheme } from '@design-system/theme';
import { getBpNum } from '../utils/getBpNum';
export const useGrid = ({ columnsInMobile, displayGrid, inputRef, overrideColGap, overrideNumCols, overrideRowGap, }) => {
    const theme = useTheme();
    const colGap = useMemo(() => overrideColGap !== null && overrideColGap !== void 0 ? overrideColGap : theme.grid.colGap, [overrideColGap]);
    const numCols = useMemo(() => overrideNumCols !== null && overrideNumCols !== void 0 ? overrideNumCols : theme.grid.numCols, [overrideNumCols]);
    const rowGap = useMemo(() => overrideRowGap !== null && overrideRowGap !== void 0 ? overrideRowGap : theme.grid.rowGap, [overrideRowGap]);
    const gridRef = useRef(null);
    const defaultGridState = useMemo(() => {
        var _a, _b;
        return ({
            colGap: getBpNum(colGap, '_'),
            columnsInMobile: Boolean(columnsInMobile),
            gridWidth: (_b = (_a = gridRef.current) === null || _a === void 0 ? void 0 : _a.clientWidth) !== null && _b !== void 0 ? _b : 0,
            numCols: getBpNum(numCols, '_'),
            rowGap: get(rowGap, '_', rowGap),
        });
    }, [colGap, columnsInMobile, numCols, gridRef.current, rowGap]);
    const [gridState, setGridState] = useState(defaultGridState);
    const queries = useMemo(() => system({
        colGap: true,
        numCols: true,
        rowGap: true,
    })({
        colGap,
        numCols,
        rowGap,
        theme,
    }), [colGap, numCols, rowGap]);
    const matchMedias = useMemo(() => reduce(queries, (result, _, query) => typeof window !== 'undefined' && /@media/u.test(query)
        ? result.concat({
            matchMedia: window.matchMedia(query.replace('@media ', '')),
            query,
        })
        : result, []), [queries]);
    useLayoutEffect(() => {
        const handleResize = () => {
            const matches = findLast(matchMedias, ({ matchMedia }) => matchMedia.matches);
            setGridState((prevState) => {
                var _a;
                const newState = matches
                    ? merge({}, defaultGridState, queries[matches.query], {
                        gridWidth: (_a = gridRef.current) === null || _a === void 0 ? void 0 : _a.clientWidth,
                    })
                    : null;
                return isEqual(newState, prevState) ? prevState : newState;
            });
        };
        const handleResizeDebounced = debounce(handleResize, 100);
        handleResize();
        window.addEventListener('resize', handleResizeDebounced);
        return () => {
            window.removeEventListener('resize', handleResizeDebounced);
        };
    }, [matchMedias]);
    const [displayGridHelper, setDisplayGridHelper] = useState(Boolean(displayGrid));
    useEffect(() => {
        setDisplayGridHelper(Boolean(displayGrid));
    }, [displayGrid]);
    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'G') {
                e.preventDefault();
                setDisplayGridHelper((prevState) => !prevState);
            }
        };
        if (process.env.NODE_ENV !== 'production') {
            document.addEventListener('keydown', handleKeyDown, true);
        }
        return () => {
            if (process.env.NODE_ENV !== 'production') {
                document.removeEventListener('keydown', handleKeyDown, true);
            }
        };
    }, []);
    return {
        displayGridHelper,
        gridContextValue: gridState !== null && gridState !== void 0 ? gridState : defaultGridState,
        ref: mergeRefs([inputRef, gridRef]),
    };
};
//# sourceMappingURL=useGrid.js.map